import { validateConfig } from '../config/validateConfig';
import { EventBus } from '../events/EventBus';
import { MicController } from '../mic/MicController';
import { AudioInput } from '../audio/AudioInput';
import { AudioOutput } from '../audio/AudioOutput';
import { CloudTextToSpeechAudioOutputProvider } from '../audio/CloudTextToSpeechAudioOutputProvider';
import { PlayButtonController } from '../audio/PlayButtonController';
import { SilenceDetector } from '../audio/SilenceDetector';
import { ConversationApiError, ConversationClient } from '../conversation/ConversationClient';
import { DEFAULT_API_BASE_URL } from '../conversation/constants';
import { BrowserGpsLocationProvider } from '../location/BrowserGpsLocationProvider';
import type { LocationProvider } from '../location/LocationProvider';
import { OutputRenderer } from '../output/OutputRenderer';
import { StateMachine } from '../state/StateMachine';
import { generateSessionId } from '../util/id';
import {
  KiaAssistantError,
  type AssistantState,
  type KiaAssistantConfig,
  type LocationContext,
} from '../types';

interface InternalEvents extends Record<string, unknown> {
  stateChange: { state: AssistantState; previous: AssistantState };
}

/**
 * One SDK instance owns exactly the three configured elements and nothing
 * else in the page. All DOM writes are funneled through OutputRenderer,
 * MicController, and PlayButtonController so this class never touches the
 * DOM directly outside of element resolution.
 */
export class KiaAssistantSDK {
  private readonly stateMachine = new StateMachine();
  private readonly bus = new EventBus<InternalEvents>();
  private readonly abortController = new AbortController();

  private readonly outputRenderer: OutputRenderer;
  private readonly audioOutput: AudioOutput;
  private readonly audioInput = new AudioInput();
  private readonly silenceDetector = new SilenceDetector();
  private readonly conversationClient: ConversationClient;
  private readonly sessionId = generateSessionId();
  private readonly micController: MicController;
  private readonly playButtonController: PlayButtonController;
  private readonly config: KiaAssistantConfig;
  private readonly locationProvider: LocationProvider | null;

  private destroyed = false;
  /** Incremented per conversation turn so a stale/superseded response is ignored. */
  private requestSeq = 0;
  private activeAbortController: AbortController | null = null;
  /** Set on first location resolution attempt; memoizes the provider to at most one call per session. */
  private locationPromise: Promise<LocationContext | undefined> | null = null;
  /** True once a response with sessionLimitReached has been rendered but its voice output hasn't finished (or been skipped) yet — see notifyPendingSessionLimit(). */
  private pendingSessionLimitReached = false;

  constructor(config: KiaAssistantConfig) {
    const elements = validateConfig(config);
    this.config = config;
    this.locationProvider =
      config.locationProvider === null
        ? null
        : (config.locationProvider ?? this.defaultLocationProvider());

    const apiBaseUrl = config.apiBaseUrl ?? DEFAULT_API_BASE_URL;
    this.outputRenderer = new OutputRenderer(elements.outputElement, {
      onMapRender: config.onMapRender,
      onWeatherRender: config.onWeatherRender,
    });
    this.audioOutput = new AudioOutput(
      config.audioOutputProvider ?? new CloudTextToSpeechAudioOutputProvider({ apiBaseUrl }),
    );
    this.conversationClient = new ConversationClient({ apiBaseUrl });

    this.stateMachine.onChange((state, previous) => {
      this.bus.emit('stateChange', { state, previous });
      this.config.onStateChange?.(state);
    });

    this.micController = new MicController(
      elements.micElement,
      {
        onActivate: () => this.handleMicActivate(),
        onDeactivate: () => this.handleMicDeactivate(),
      },
      this.abortController.signal,
    );

    this.playButtonController = new PlayButtonController(
      elements.playElement,
      {
        getText: () => this.outputRenderer.getLastText(),
        getLanguage: () => this.outputRenderer.getLastLanguage(),
        onPlay: (text, language) => this.handlePlay(text, language),
        onStop: () => this.handleStop(),
      },
      this.abortController.signal,
    );
  }

  /**
   * GPS only — no IP-based fallback. When GPS is denied/unavailable, no
   * location is sent at all; the LLM asks the user for it directly if a
   * location-dependent question comes up.
   */
  private defaultLocationProvider(): LocationProvider {
    return new BrowserGpsLocationProvider();
  }

  getState(): AssistantState {
    return this.stateMachine.getState();
  }

  private handleMicActivate(): void {
    if (this.stateMachine.getState() !== 'READY') return;
    // Must happen synchronously, inside this real click handler, before any
    // `await` — see AudioOutputProvider.unlock(). A second, defense-in-depth
    // call alongside the one in open(): if a publisher ever calls open()
    // itself outside a genuine user gesture, this tap is still guaranteed
    // to be one.
    this.audioOutput.unlock();
    this.stateMachine.transition('LISTENING');
    this.audioInput
      .start()
      .then(() => this.beginSilenceDetection())
      .catch((error: unknown) => {
        this.reportError(error, 'MIC_CAPTURE_FAILED');
        if (this.stateMachine.getState() === 'LISTENING') {
          this.stateMachine.transition('ERROR');
        }
      });
  }

  /**
   * Auto-stops listening once the mic has picked up nothing but silence for
   * a few seconds — including from the very start, if the user never speaks
   * at all — so the turn submits itself instead of requiring a second tap.
   * A no-op wherever Web Audio API support is missing; manual stop (a
   * second mic tap) always still works regardless.
   */
  private beginSilenceDetection(): void {
    if (this.stateMachine.getState() !== 'LISTENING') return;
    const stream = this.audioInput.getStream();
    if (!stream) return;
    this.silenceDetector.start(
      stream,
      () => {
        if (this.stateMachine.getState() !== 'LISTENING') return;
        this.micController.deactivate();
      },
      {
        // TEMPORARY calibration aid — remove once volumeThreshold is
        // confirmed to work reliably against real hardware. Watch this in
        // the browser console while talking vs. staying quiet.
        onLevel: (peak, isSilent) => {
          console.debug(
            `[Kia mic level] peak=${peak.toFixed(3)} ${isSilent ? '(silent)' : '(sound)'}`,
          );
        },
      },
    );
  }

  private handleMicDeactivate(): void {
    if (this.stateMachine.getState() !== 'LISTENING') return;
    this.silenceDetector.stop();
    // Stop capture and move to PROCESSING before any async work, so mic
    // capture is never still running once a Gemini request is in flight.
    this.stateMachine.transition('PROCESSING');
    void this.runConversationTurn();
  }

  private async runConversationTurn(): Promise<void> {
    const seq = ++this.requestSeq;
    const controller = new AbortController();
    this.activeAbortController = controller;

    const stillCurrent = (): boolean =>
      !this.destroyed && seq === this.requestSeq && this.stateMachine.getState() === 'PROCESSING';

    try {
      const [captured, location] = await Promise.all([
        this.audioInput.stop(),
        this.resolveLocation(),
      ]);
      if (!captured) {
        throw new KiaAssistantError('No audio was captured.', 'NO_AUDIO_CAPTURED');
      }

      const response = await this.conversationClient.sendAudio(
        this.sessionId,
        captured,
        location,
        controller.signal,
      );
      if (!stillCurrent()) return;

      // The transcript (what the user was understood to have said) is
      // deliberately never shown — only the assistant's own response.
      this.outputRenderer.clear();
      this.outputRenderer.renderResponse(response);
      this.stateMachine.transition('READY');
      this.pendingSessionLimitReached = response.sessionLimitReached;
      this.autoPlayResponse();
    } catch (error) {
      if (!stillCurrent()) return;
      this.reportError(error, 'CONVERSATION_FAILED');
      this.stateMachine.transition('ERROR');
    } finally {
      if (this.activeAbortController === controller) {
        this.activeAbortController = null;
      }
    }
  }

  /**
   * Explicit, host-supplied location always wins. Otherwise, the
   * locationProvider (GPS by default) is queried once permission has
   * actually been granted — see resolveLocationOnce()/warmUpPermissions() —
   * and cached from then on, so a turn is never blocked on a fresh
   * permission round-trip and a granted permission never re-prompts. Before
   * that, a failed attempt (e.g. the warm-up call racing an unanswered
   * permission dialog) is retried on the next turn rather than sticking
   * with "no location" for the rest of the session.
   */
  private async resolveLocation(): Promise<LocationContext | undefined> {
    if (this.config.location) return this.config.location;
    if (!this.locationProvider) return undefined;
    return this.resolveLocationOnce();
  }

  /**
   * Memoized, but only on success: once the provider actually yields a
   * location, it's cached for the rest of the session (no re-prompting).
   * A null/failed attempt — e.g. the very first (warm-up) call racing a
   * permission dialog the user hadn't answered yet — is never cached, so
   * the next turn that actually needs a location tries again instead of
   * being stuck with "no location" for the whole session.
   */
  private resolveLocationOnce(): Promise<LocationContext | undefined> {
    if (!this.locationPromise) {
      this.locationPromise = (async () => {
        try {
          const location = await this.locationProvider?.getLocation();
          if (!location) this.locationPromise = null;
          return location ?? undefined;
        } catch {
          this.locationPromise = null;
          return undefined;
        }
      })();
    }
    return this.locationPromise;
  }

  /**
   * Proactively requests mic and location permission once, right when the
   * SDK becomes active, instead of waiting for the user's first mic tap (mic)
   * or first conversation turn (location). Sequenced deliberately — mic
   * fully settles (granted or denied) before geolocation is even requested
   * — so the browser only ever shows one permission prompt at a time in a
   * fixed 1) microphone, 2) location order, rather than both firing at once
   * and racing to see which dialog the browser stacks first. Fires
   * onMicrophonePermission/onLocationPermission right after each one
   * settles, so a publisher's creative can sequence its own flow around
   * them instead of guessing. Fire-and-forget from open()'s perspective: a
   * denial/failure here is not fatal — it just means the corresponding
   * capability is unavailable when actually needed, exactly as if warm-up
   * had never run.
   */
  private async warmUpPermissions(): Promise<void> {
    const micGranted = await this.audioInput
      .ensureStream()
      .then(() => true)
      .catch(() => false);
    this.config.onMicrophonePermission?.({ granted: micGranted });

    if (!this.config.location && this.locationProvider) {
      const location = await this.resolveLocationOnce();
      this.config.onLocationPermission?.({ granted: Boolean(location) });
    }
  }

  /**
   * Speaks the response the moment it's ready, so the host never needs a
   * manual Play tap for the normal flow — the play button/controller still
   * works too (e.g. to replay), since it drives through this same
   * handlePlay() and its READY-only guard makes the two paths mutually safe.
   */
  private autoPlayResponse(): void {
    const text = this.outputRenderer.getLastText();
    if (!text) {
      // Nothing to speak, so "once voice output ends" is immediate.
      this.notifyPendingSessionLimit();
      return;
    }
    this.handlePlay(text, this.outputRenderer.getLastLanguage());
  }

  private handlePlay(text: string, language: string | null): void {
    if (this.stateMachine.getState() !== 'READY') return;
    this.stateMachine.transition('SPEAKING');
    // Keeps the play button's own speaking flag in sync even when this
    // playback wasn't started by its click (auto-play) — so a later click
    // on it still correctly stops playback instead of trying to start one.
    this.playButtonController.notifyPlaybackStarted();
    this.audioOutput
      .speak(text, language)
      .catch((error: unknown) => this.reportError(error, 'AUDIO_OUTPUT_FAILED'))
      .finally(() => {
        this.playButtonController.notifyPlaybackEnded();
        if (this.stateMachine.getState() === 'SPEAKING') {
          this.stateMachine.transition('READY');
        }
        this.notifyPendingSessionLimit();
      });
  }

  /**
   * Fires onSessionLimitReached exactly once, right after voice output for
   * the response that reached the limit has finished (naturally, via
   * stop(), or via an audio-output failure — all three count as "the voice
   * output ended"). The flag resets immediately so a later manual replay of
   * the same response (or any later turn) never re-fires it.
   */
  private notifyPendingSessionLimit(): void {
    if (!this.pendingSessionLimitReached) return;
    this.pendingSessionLimitReached = false;
    this.config.onSessionLimitReached?.();
  }

  private handleStop(): void {
    this.audioOutput.stop();
    if (this.stateMachine.getState() === 'SPEAKING') {
      this.stateMachine.transition('READY');
    }
  }

  private reportError(error: unknown, code: string): void {
    const wrapped =
      error instanceof KiaAssistantError
        ? error
        : error instanceof ConversationApiError
          ? new KiaAssistantError(error.message, error.code)
          : new KiaAssistantError(error instanceof Error ? error.message : String(error), code);
    this.config.onError?.(wrapped);
  }

  open(): void {
    this.assertNotDestroyed();
    if (this.stateMachine.getState() !== 'IDLE') return;
    // Must happen synchronously, before any `await` — see
    // AudioOutputProvider.unlock(). Only actually unlocks anything on iOS
    // Safari if the publisher calls open() directly from a click handler
    // (the documented/intended integration pattern); handleMicActivate()
    // repeats this as a fallback for whenever that isn't the case.
    this.audioOutput.unlock();
    this.stateMachine.transition('INITIALIZING');
    this.stateMachine.transition('READY');
    void this.warmUpPermissions();
  }

  close(): void {
    this.assertNotDestroyed();
    if (this.stateMachine.getState() === 'IDLE') return;
    this.stopActiveInteractions();
    if (this.stateMachine.canTransition('IDLE')) {
      this.stateMachine.transition('IDLE');
    }
  }

  reset(): void {
    this.assertNotDestroyed();
    const wasOpen = this.stateMachine.getState() !== 'IDLE';
    this.stopActiveInteractions();
    this.outputRenderer.clear();
    if (
      wasOpen &&
      this.stateMachine.getState() !== 'READY' &&
      this.stateMachine.canTransition('READY')
    ) {
      this.stateMachine.transition('READY');
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.stopActiveInteractions();
    this.audioInput.dispose();
    this.audioOutput.dispose();
    this.abortController.abort();
    this.micController.destroy();
    this.playButtonController.destroy();
    this.outputRenderer.destroy();
    this.bus.clear();
    this.stateMachine.reset();
    this.destroyed = true;
  }

  private stopActiveInteractions(): void {
    this.silenceDetector.stop();
    if (this.micController.isActive()) {
      this.micController.deactivate();
    }
    // Invalidate any in-flight conversation turn so its result/error is
    // ignored if it resolves after close()/reset()/destroy() moved us on.
    this.requestSeq += 1;
    this.activeAbortController?.abort();
    this.activeAbortController = null;
    this.audioOutput.stop();
    this.playButtonController.notifyPlaybackEnded();
  }

  private assertNotDestroyed(): void {
    if (this.destroyed) {
      throw new KiaAssistantError('KiaAssistant instance has been destroyed.', 'DESTROYED');
    }
  }
}
